import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, KPI, Loading, ErrorBox, StatusBadge, PhaseBadge, Empty } from "@/components/common";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useI18n, STATUS } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function Dashboard() {
  const { tx, lbl } = useI18n();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))); }, []);
  if (err) return <ErrorBox error={err} />;
  if (!d) return <Loading />;
  const s = d.interviews_by_status;
  const total = Object.values(s).reduce((a, b) => a + b, 0);
  const done = s.submitted + s.in_review + s.approved;
  return (
    <div>
      <PageHeader eyebrow={tx("Accueil", "Home")} title={`${tx("Bonjour", "Hello")}, ${user.name.split(" ").slice(-2).join(" ")}`}
        subtitle={tx("Vue consolidée de vos missions autorisées uniquement.", "Consolidated view of your authorised missions only.")} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI testid="kpi-clients" label={tx("Clients", "Clients")} value={d.clients} />
        <KPI testid="kpi-audits" label={tx("Audits actifs", "Active audits")} value={d.active_audits} sub={`${d.audits} ${tx("au total", "total")}`} />
        <KPI testid="kpi-interviews" label={tx("Entretiens", "Interviews")} value={total} sub={`${done} ${tx("soumis/approuvés", "submitted/approved")}`} />
        <KPI testid="kpi-approved" label={tx("Approuvés", "Approved")} value={s.approved} sub={`n = ${total}`} />
      </div>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <section className="card-surface">
          <div className="px-4 py-3 border-b border-slate-200 font-display font-semibold text-[#153A63]">{tx("Mes entretiens ouverts", "My open interviews")}</div>
          {d.my_open_interviews.length === 0 ? <Empty text={tx("Aucun entretien ouvert", "No open interviews")} /> : (
            <table className="tbl">
              <tbody>
                {d.my_open_interviews.map((i) => (
                  <tr key={i.id}>
                    <td><Link data-testid={`dash-interview-${i.id}`} className="text-[#2176AE] hover:underline font-mono text-xs" to={`/interviews/${i.id}`}>{i.employee_label}</Link></td>
                    <td><StatusBadge status={i.status} /></td>
                    <td className="text-xs text-slate-500">{i.scheduled_at ? fmtDate(i.scheduled_at) : `${tx("Échéance", "Due")} ${i.due_date || "—"}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        <section className="card-surface p-4">
          <div className="font-display font-semibold text-[#153A63] mb-3">{tx("Pipeline des entretiens", "Interview pipeline")}</div>
          <div className="space-y-2">
            {Object.keys(STATUS).map((k) => (
              <div key={k} className="flex items-center gap-3 text-sm" data-testid={`pipeline-${k}`}>
                <span className="w-32 text-slate-600">{lbl(STATUS, k)}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded"><div className="h-2 bg-[#2176AE] rounded" style={{ width: total ? `${(100 * s[k]) / total}%` : 0 }} /></div>
                <span className="w-8 text-right font-mono text-xs">{s[k]}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="card-surface mt-6">
        <div className="px-4 py-3 border-b border-slate-200 font-display font-semibold text-[#153A63]">{tx("Audits récents", "Recent audits")}</div>
        {d.recent_audits.length === 0 ? <Empty text={tx("Aucun audit", "No audits")} /> : (
          <table className="tbl"><tbody>
            {d.recent_audits.map((a) => (
              <tr key={a.id}>
                <td><Link className="text-[#2176AE] hover:underline font-medium" data-testid={`dash-audit-${a.id}`} to={`/audits/${a.id}`}>{a.title}</Link></td>
                <td><PhaseBadge phase={a.phase} /></td>
                <td className="text-xs text-slate-500">{a.period}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </section>
    </div>
  );
}
